import { batch } from 'react-redux';
import cogoToast from 'cogo-toast';

import CaseUtil from '@common/lib/caseUtil';
import homeAPI from '@referrals/api/home';

const GET_INIT = 'referrals/home/GET_INIT';
const GET_DONE = 'referrals/home/GET_DONE';
const GET_ERROR = 'referrals/home/GET_ERROR';
const TOGGLE_VALUE = 'referrals/home/TOGGLE_VALUE';
const VERIFIED_PHONE = 'referrals/home/VERIFIED_PHONE';
const MARK_USER_CONSENT = 'referrals/home/MARK_USER_CONSENT';
const TOGGLE_PROGRAM_SELECT = 'referrals/home/TOGGLE_PROGRAM_SELECT';

export function initialLoad() {
  return async (dispatch, getState) => {
    const { isLoading } = getState().home;
    if (isLoading) return;

    dispatch({ type: GET_INIT });
    try {
      const response = await homeAPI.initialLoad();
      const data = CaseUtil.toCase('camelCase', response);
      const { rcbCount, enrolledCount } = data;

      const withoutRcb = rcbCount === 0;
      const withOneRcb = rcbCount > 0 && enrolledCount === 0;
      const withOneReferral = enrolledCount === 1;
      const withMoreThanOneReferral = !withoutRcb
        && !withOneRcb
        && !withOneReferral;

      dispatch({
        type: GET_DONE,
        payload: {
          ...data,
          withoutRcb,
          withOneRcb,
          withOneReferral,
          withMoreThanOneReferral,
        },
      });
    } catch (error) {
      cogoToast.error('Something Went Wrong! Please reload the page');
      dispatch({ type: GET_ERROR });
    }
  };
}

export function markUserConsent() {
  return async dispatch => {
    await homeAPI.markUserConsent();
    dispatch({ type: MARK_USER_CONSENT });
  };
}

export function closeBankBanner() {
  return async dispatch => {
    dispatch({ type: TOGGLE_VALUE, key: 'isBankBannerClosed', value: true });
  };
}

function updateTimer(value) {
  return async dispatch => {
    dispatch({ type: TOGGLE_VALUE, key: 'timer', value });
  };
}

export function toggleMeta(key, value) {
  return {
    type: TOGGLE_VALUE,
    key,
    value,
  };
}

function setTimer() {
  return async dispatch => {
    let currentTime = 60;
    dispatch(updateTimer(60));
    const timerFunc = setInterval(() => {
      dispatch(updateTimer(currentTime - 1));
      currentTime -= 1;
      if (currentTime < 0) {
        clearInterval(timerFunc);
      }
    }, 1000);
  };
}

export function requestOtp(token = '') {
  return async (dispatch, getState) => {
    batch(() => {
      dispatch({ type: TOGGLE_VALUE, key: 'isRequestingOtp', value: true });
    });
    try {
      const { phoneNumber, email, hasRequestedOtp } = {
        phoneNumber: getState().phoneVerificationForm.fields.phone_number,
        email: getState().home.email,
        hasRequestedOtp: getState().home.hasRequestedOtp,
      };
      await homeAPI.requestOtp({
        phoneNumber, email, resend: hasRequestedOtp, token,
      });
      batch(() => {
        dispatch({ type: TOGGLE_VALUE, key: 'hasRequestedOtp', value: true });
        dispatch(setTimer());
        dispatch({ type: TOGGLE_VALUE, key: 'isRequestingOtp', value: false });
      });
      cogoToast.success('OTP sent!!');
    } catch (error) {
      let message = 'Something went wrong! Please try again in sometime.';
      switch (error.response.status) {
        case 422:
          message = error.responseJson?.message || message;
          break;
        case 409:
          message = 'Phone number is linked to a different email';
          break;
        default:
          // do nothing
      }
      cogoToast.error(message);
      dispatch({ type: TOGGLE_VALUE, key: 'isRequestingOtp', value: false });
    }
  };
}

export function verifyOtp() {
  return async (dispatch, getState) => {
    dispatch({ type: TOGGLE_VALUE, key: 'isSubmittingOtp', value: true });
    dispatch({ type: TOGGLE_VALUE, key: 'invalidOtp', value: false });
    try {
      const { phoneNumber, otp } = {
        phoneNumber: getState().phoneVerificationForm.fields.phone_number,
        otp: getState().otpForm.fields.otp,
      };
      await homeAPI.verifyOtp({ phoneNumber, otp });
      cogoToast.success('Phone Verified!!');
      batch(() => {
        dispatch({ type: VERIFIED_PHONE });
        dispatch({ type: TOGGLE_VALUE, key: 'isSubmittingOtp', value: false });
      });
    } catch (error) {
      dispatch({ type: TOGGLE_VALUE, key: 'isSubmittingOtp', value: false });
      if (error.response && error.response.status === 403) {
        dispatch({ type: TOGGLE_VALUE, key: 'invalidOtp', value: true });
      } else {
        cogoToast.error('Something Went Wrong!');
      }
    }
  };
}

export function createActionTracking(actionLabel) {
  return async () => {
    try {
      await homeAPI.createActionTracking(actionLabel);
    } catch (error) {
      cogoToast.error('Something Went Wrong!');
    }
  };
}

export function toggleReferralProgramSelect(value) {
  return async (dispatch) => {
    dispatch({ type: TOGGLE_PROGRAM_SELECT, value });
  };
}

export default function reducer(
  state = {
    isLoading: false,
    loadError: null,
    couponCode: '',
    email: '',
    userName: '',
    userSlug: '',
    phoneVerified: false,
    phoneNumber: '',
    isRequestingOtp: false,
    isSubmittingOtp: false,
    hasRequestedOtp: false,
    timer: 60,
    hasBankDetails: true,
    isBankBannerClosed: false,
    invalidOtp: false,
    referralStats: {
      referrers: '-',
      referees: '-',
    },
    eligibleForQna: false,
    referralManager: null,
    withOneRcb: false,
    withOneReferral: false,
    withMoreThanOneReferral: false,
    withoutRcb: false,
    consentStatus: false,
    howReferralWorksVisited: false,
    rewardsCalculatorVisited: false,
    firstVisit: false,
    isSstMentee: null,
    isSsbApplicant: null,
    celebratoryModalActive: false,
    celebratoryModalType: null,
    showReferralProgramSelect: true,
  },
  action,
) {
  switch (action.type) {
    case GET_INIT:
      return { ...state, isLoading: true, loadError: null };
    case GET_DONE:
      return {
        ...state,
        isLoading: false,
        loadError: null,
        ...action.payload,
      };
    case GET_ERROR:
      return { ...state, isLoading: false, loadError: true };
    case TOGGLE_VALUE:
      return { ...state, [action.key]: action.value };
    case VERIFIED_PHONE:
      return { ...state, phoneVerified: true };
    case MARK_USER_CONSENT:
      return { ...state, consentStatus: true };
    case TOGGLE_PROGRAM_SELECT:
      return { ...state, showReferralProgramSelect: action.value };
    default:
      return state;
  }
}
